import { graphql } from 'gatsby';
import Img from 'gatsby-image';
import React from 'react';
import tw from 'tailwind.macro';
import Button from '../components/button';
import Layout from '../components/layout';
import LeadText from '../components/leadText';
import Section from '../components/section';
import SEO from '../components/seo';
import PageNavigator from '../components/pageNavigator';
import SafeLink from '../components/safeLink';
import Grid from '../components/grid';

const Conference2026 = ({ data, location }) => {
  const parentTitle = 'The Centre for Social Justice presents:';
  const pageTitle = 'The 7th International Social Justice Conference';

  return (
    <Layout location={location} parentTitle={parentTitle} pageTitle={pageTitle}>
      <SEO title={pageTitle} image={data.hero.childImageSharp.fluid.src} />
      <div css={[tw`relative flex items-center py-10`, `min-height:30vh;`]}>
        <div css={tw`absolute inset-0 z-0`}>
          <video
            autoPlay
            muted
            loop
            playsInline
            css={tw`w-full h-full object-cover`}
          >
            <source src="/adobestock_1478554150-2.webm" type="video/webm" />
            <source src="/adobestock_1478554150-2.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
        <div className="relative container">
          <div>
            <LeadText
              css={[
                tw`my-0 uppercase tracking-wide text-white`,
                `text-shadow:0 0 10px rgba(0,0,0,0.5)`,
              ]}
              className="text-shadow"
            >
              {parentTitle}
            </LeadText>
            <h1
              className="text-shadow"
              css={[
                tw`my-0 text-white tracking-wider`,
                `text-shadow:0 0 10px rgba(0,0,0,0.5)`,
              ]}
            >
              {pageTitle}
            </h1>
          </div>
        </div>
      </div>
      <PageNavigator
        items={{
          welcome: 'Welcome',
          concept: 'Concept',
          themes: 'Themes',
          abstracts: 'Abstracts',
          registration: 'Registration',
          previous: 'Previous Conferences',
          contact: 'Contact',
        }}
        buttonText="Register now"
        buttonLink="https://www.quicket.co.za/events/384392-7th-international-social-justice-conference/?ref=events-list#/"
      />
      <Section id="welcome">
        <Grid lgGridCols={2} lgGap={5}>
          <div>
            <h2>Welcome!</h2>
            <h3>
              The theme of the conference is{' '}
              <em>
                Decoloniality, Constitutional Transformation and Social Justice
                in the Global South: Indigenous Systems, Food Sovereignty and
                Pathways to Substantive Equality.
              </em>
            </h3>
            <ul>
              <li>
                <strong>Date:</strong> 24 August 2026
              </li>
              <li>
                <strong>Venue:</strong> Stellenbosch Institute for Advanced
                Study (STIAS), Stellenbosch, South Africa
              </li>
              <li>
                <strong>Format:</strong> Hybrid (in-person and virtual
                participation)
              </li>
            </ul>
            <Button
              to="https://www.quicket.co.za/events/384392-7th-international-social-justice-conference/?ref=events-list#/"
              size="md"
              css={tw`mb-2 mr-2`}
            >
              Register now
            </Button>
            <Button
              to="/downloads/events/2026-08-conference-call-for-papers.pdf"
              size="md"
              css={tw`mb-2 mr-2 bg-dark-gray`}
            >
              Download call for papers
            </Button>
          </div>
          <Img
            fluid={data.Conference2026Poster.childImageSharp.fluid}
            alt="The 7th International Social Justice Conference, 24 August 2026, Stellenbosch Institute for Advanced Study (STIAS)"
            css={tw`rounded-lg shadow-lg`}
          />
        </Grid>
      </Section>
      <Section id="concept">
        <h2>Concept</h2>
        <p>
          The 7th International Social Justice Conference (7th ISJC) invites
          scholars, jurists, researchers, policymakers, development
          practitioners, indigenous knowledge scholars and civil society actors
          to critically engage questions relating to constitutional
          transformation, decoloniality, indigenous systems and social justice
          within the Global South.
        </p>
        <p>
          Thirty years into constitutional democracy in South Africa under a
          Constitution that entrenches social justice as one of the pillars of
          the blueprint the Constitution seeks to establish, social justice and
          related equal enjoyment of all rights and freedoms on a level playing
          field seem elusive.
        </p>
        <p>
          The structural inequalities that undermine social justice are not
          unique to South Africa but are part of a broader global context marked
          by persistent and intergenerationally transmitted forms of inequality,
          socio-economic disadvantage and exclusion, food insecurity, ecological
          vulnerability, and governance. Such disparities remain shaped by
          histories of colonialism, dispossession and legalised injustice. This
          is despite constitutions hailed as blueprints for transformative
          governance undergirded by transformative constitutionalism and
          socially just sustainable development.
        </p>
        <p>
          One of the enduring structural inequality legacies of previously
          legalised injustices such as colonialism, is systemic inequality
          between the Global North and the Global South impacting on equal
          enjoyment of political, economic, social and cultural rights on an
          equal footing within and between societies, with the Global South
          visibly continuing to bear the yoke of colonial developmental
          barriers, reflected in epistemic hegemonies, culturally unfit legal
          systems and the marginalisation of indigenous systems of governance
          and production, including food production.
        </p>
        <p>
          These realities raise important questions regarding the extent to
          which constitutional democracies and governance systems have
          meaningfully advanced substantive equality, social justice and
          equitable development, particularly within contexts characterised by
          structural inequality, food insecurity, ecological vulnerability, and
          unequal systems of production and distribution.
        </p>
        <p>Read the full call for abstracts below.</p>
        <Button
          to="/downloads/events/2026-08-conference-call-for-papers.pdf"
          size="md"
          css={tw`mb-2 mr-2 bg-dark-gray`}
        >
          Download call for papers
        </Button>
      </Section>
      <Section id="themes" isShaded>
        <h2>Thematic conference streams</h2>
        <LeadText>
          Abstracts are invited in, but are not limited to, the following
          thematic areas. Interdisciplinary, comparative, and practice-oriented
          contributions grounded in social justice constitutional principles are
          particularly encouraged.
        </LeadText>
        <Grid>
          <div css={tw`flex flex-col p-5 bg-white rounded-lg shadow-lg`}>
            <h3>
              1. Transformative Constitutionalism, Structural Inequality and
              Social Justice
            </h3>
            <ul>
              <li>transformative constitutionalism and substantive equality;</li>
              <li>restorative and redistributive constitutionalism;</li>
              <li>
                constitutional governance, social justice and socio-economic
                rights;
              </li>
              <li>constitutional accountability and implementation;</li>
              <li>law, inequality and structural exclusion;</li>
              <li>constitutionalism and lived realities; and</li>
              <li>judicial approaches to transformative justice.</li>
            </ul>
          </div>
          <div css={tw`flex flex-col p-5 bg-white rounded-lg shadow-lg`}>
            <h3>
              2. Decoloniality, Constitutional Futures and Epistemic Justice
            </h3>
            <ul>
              <li>decolonial constitutionalism;</li>
              <li>epistemic justice and legal knowledge systems;</li>
              <li>decoloniality, governance and development;</li>
              <li>decolonial political economy and systems of production;</li>
              <li>decolonising law and public institutions;</li>
              <li>constitutional futures in the Global South; and</li>
              <li>law, power and structural transformation.</li>
            </ul>
          </div>
          <div css={tw`flex flex-col p-5 bg-white rounded-lg shadow-lg`}>
            <h3>3. Indigenous Systems, Governance and Legal Pluralism</h3>
            <ul>
              <li>
                indigenous knowledge systems, constitutional governance and
                democratic accountability;
              </li>
              <li>
                indigenous systems, constitutional transformation and food
                sovereignty;
              </li>
              <li>legal pluralism, customary law and substantive equality;</li>
              <li>
                traditional leadership, governance and constitutional
                accountability;
              </li>
              <li>customary law, gender justice and intersectional equality;</li>
              <li>
                indigenous systems, sustainability and transformative
                development; and
              </li>
              <li>
                critical perspectives on indigenous systems within
                constitutional democracies.
              </li>
            </ul>
          </div>
          <div css={tw`flex flex-col p-5 bg-white rounded-lg shadow-lg`}>
            <h3>
              4. Food Sovereignty, Food Justice and Sustainable Development
            </h3>
            <ul>
              <li>food sovereignty and constitutional rights;</li>
              <li>right to food and transformative constitutionalism;</li>
              <li>food justice and socio-economic inequality;</li>
              <li>indigenous food systems and ecological sustainability;</li>
              <li>climate justice and sustainable livelihoods;</li>
              <li>local economies and community resilience; and</li>
              <li>
                governance, food systems and constitutional political economy.
              </li>
            </ul>
          </div>
          <div css={tw`flex flex-col p-5 bg-white rounded-lg shadow-lg`}>
            <h3>
              5. Governance, Institutional Accountability and Transformative
              Development
            </h3>
            <ul>
              <li>governance and constitutional accountability;</li>
              <li>local government and transformative development;</li>
              <li>public institutions and socio-economic rights;</li>
              <li>governance failures and structural inequality;</li>
              <li>social justice impact assessment and policy design;</li>
              <li>
                governance innovation and transformative governance; and
              </li>
              <li>governance systems and socially responsive development.</li>
            </ul>
          </div>
          <div css={tw`flex flex-col p-5 bg-white rounded-lg shadow-lg`}>
            <h3>6. Restorative and Redistributive Pathways to Social Justice</h3>
            <ul>
              <li>restorative constitutionalism and substantive equality;</li>
              <li>redistributive justice and substantive equality;</li>
              <li>recognition and redistribution;</li>
              <li>ubuntu and transformative governance;</li>
              <li>reparative approaches to historical injustice;</li>
              <li>access to justice and social inclusion; and</li>
              <li>
                pathways toward equitable, sustainable and socially just
                futures.
              </li>
            </ul>
          </div>
        </Grid>
      </Section>
      <Section id="abstracts">
        <h2>Call for abstracts</h2>
        <p>
          Scholars, jurists, researchers, policymakers, development
          practitioners, indigenous knowledge scholars and civil society actors
          are invited to submit abstracts of{' '}
          <strong>300&ndash;400 words</strong> to{' '}
          <SafeLink
            to="mailto:socialjustice@sun.ac.za"
            css={tw`border-b-2 border-maroon hover:text-maroon`}
          >
            socialjustice@sun.ac.za
          </SafeLink>
          .
        </p>
        <h3>Submission guidelines</h3>
        <ul>
          <li>
            Indicate the theme under which the submission is made (see the
            thematic streams above).
          </li>
          <li>
            <strong>Abstract length:</strong> 300&ndash;400 words.
          </li>
          <li>
            <strong>Author details:</strong> include your full name,
            institutional affiliation and a short biography (maximum 150 words).
          </li>
          <li>
            <strong>Submission email:</strong>{' '}
            <SafeLink
              to="mailto:socialjustice@sun.ac.za"
              css={tw`border-b-2 border-maroon hover:text-maroon`}
            >
              socialjustice@sun.ac.za
            </SafeLink>
          </li>
        </ul>
        <h3>Important dates</h3>
        <ul>
          <li>
            <strong>Abstract submission deadline:</strong> 21 July 2026
          </li>
          <li>
            <strong>Notification of acceptance:</strong> 30 July 2026
          </li>
          <li>
            <strong>Conference:</strong> 24 August 2026
          </li>
        </ul>
        <p>
          Accepted papers will be considered for inclusion in a post-conference
          peer-reviewed publication in partnership with JUTA.
        </p>
        <Button
          to="/downloads/events/2026-08-conference-call-for-papers.pdf"
          size="md"
          css={tw`mb-2 mr-2`}
        >
          Download call for papers
        </Button>
      </Section>
      <Section id="registration" isShaded>
        <h2>Registration</h2>
        <LeadText>
          The conference is a hybrid event hosted at the Stellenbosch Institute
          for Advanced Study (STIAS) in Stellenbosch, South Africa. Accepted
          presenters may participate either in person or virtually, and
          attendance is open to registered delegates nationally and
          internationally.
        </LeadText>
        <ul>
          <li>
            <strong>In-person attendance:</strong> R800 per person (includes
            meals and conference materials)
          </li>
          <li>
            <strong>Virtual attendance:</strong> R100 per person
          </li>
        </ul>
        <Button
          to="https://www.quicket.co.za/events/384392-7th-international-social-justice-conference/?ref=events-list#/"
          size="lg"
          css={tw`mb-2 mr-2`}
        >
          Register now
        </Button>
      </Section>
      <Section id="previous">
        <h2>Previous Conferences</h2>
        <LeadText>
          You can watch free recordings of previous summits and conferences on
          our website.
          <ul>
            <li>
              <SafeLink
                to="/summit-2020/"
                css={tw`border-b-2 border-maroon hover:text-maroon`}
              >
                Watch the 2020 summit &rarr;
              </SafeLink>
            </li>
            <li>
              <SafeLink
                to="/summit-conference-2021/"
                css={tw`border-b-2 border-maroon hover:text-maroon`}
              >
                Watch the 2021 summit and conference &rarr;
              </SafeLink>
            </li>
            <li>
              <SafeLink
                to="/conference-summit-2022/"
                css={tw`border-b-2 border-maroon hover:text-maroon`}
              >
                Watch the 2022 summit and conference &rarr;
              </SafeLink>
            </li>
            <li>
              <SafeLink
                to="/conference-summit-2023/"
                css={tw`border-b-2 border-maroon hover:text-maroon`}
              >
                Watch the 2023 summit and conference &rarr;
              </SafeLink>
            </li>
            <li>
              <SafeLink
                to="/conference-2024/"
                css={tw`border-b-2 border-maroon hover:text-maroon`}
              >
                Watch the 2024 conference &rarr;
              </SafeLink>
            </li>
            <li>
              <SafeLink
                to="/conference-2025/"
                css={tw`border-b-2 border-maroon hover:text-maroon`}
              >
                See the 2025 conference &rarr;
              </SafeLink>
            </li>
          </ul>
        </LeadText>
      </Section>
      <Section id="contact" isShaded>
        <h2>Contact</h2>
        <LeadText>
          Please direct any enquiries to the Centre for Social Justice, Faculty
          of Law, Stellenbosch University at{' '}
          <SafeLink
            to="mailto:socialjustice@sun.ac.za"
            css={tw`border-b-2 border-maroon hover:text-maroon`}
          >
            socialjustice@sun.ac.za
          </SafeLink>{' '}
          or via the contact form below.
        </LeadText>
        <form action="https://usebasin.com/f/44697ef058ef" method="POST">
          <label className="block mb-5">
            <span className="text-dark">Name:</span>
            <input
              className="form-input mt-1 block w-full"
              name="name"
              placeholder="John Smith"
            />
          </label>
          <label className="block mb-5">
            <span className="text-dark">Email:</span>
            <input
              className="form-input mt-1 block w-full"
              name="email"
              placeholder="john@smith.com"
            />
          </label>
          <label htmlFor="phone" css={tw`mb-5`}>
            <span css={tw`block`}>Phone:</span>
            <input
              name="phone"
              type="text"
              placeholder="+27 72 123 4567"
              className="form-input mt-1 block w-full"
            />
          </label>
          <label className="block mb-5">
            <span className="text-dark">Message:</span>
            <textarea
              className="form-textarea mt-1 block w-full"
              rows="3"
              name="message"
              placeholder="Your message"
            />
          </label>
          <Button type="submit" size="md">
            Submit &rarr;
          </Button>
        </form>
      </Section>
    </Layout>
  );
};

export default Conference2026;

export const pageQuery = graphql`
  query {
    hero: file(relativePath: { eq: "2026-08-conference-poster.jpg" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    Conference2026Poster: file(
      relativePath: { eq: "2026-08-conference-poster.jpg" }
    ) {
      childImageSharp {
        fluid(quality: 100) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    site {
      siteMetadata {
        title
      }
    }
  }
`;
